import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useCancellableCallback } from '../hooks/use-cancellable-callback.js';
import { useIdOrProvided } from '../hooks/use-id-or-provided.js';
import { addListener, accept } from '../utils/event.js';
import { mergeProps } from '../utils/merge-props.js';

/**
 * The threshold, in milliseconds, a user must hold their pointer down to
 * initiate a click-and-hold menu selection (a la old Macs, but still
 * supported on current macOS). macOS’ default is 400 milliseconds.
 * @default 400
 *
 * Note: this could be a prop eventually if desirable (e.g. for accessibility)
 */
const CLICK_AND_DRAG_THRESHOLD = 400;
function useMenu(options) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(options?.open ?? false);
    const id = useIdOrProvided(options?.id);
    const triggerId = `${id}-trigger`;
    const isClickHold = useRef(false);
    const anchorRefFallback = useRef(null);
    const anchorRef = options?.anchorRef ?? anchorRefFallback;
    const rootRef = useRef(null);
    const open = options?.open ?? uncontrolledOpen;
    // if controlled, sync internal state
    useEffect(() => {
        if (options?.open !== undefined && uncontrolledOpen !== options.open) {
            setUncontrolledOpen(options.open);
        }
    }, [options?.open, uncontrolledOpen]);
    const [delayedClickAndHold, cancelDelayedClickAndHold] = useCancellableCallback(() => (isClickHold.current = true), { delay: CLICK_AND_DRAG_THRESHOLD }, []);
    // controlled + uncontrolled state updates
    // Note: firing both at once saves effect thrashing
    const setOpen = useCallback((value) => {
        options?.onOpenChange?.(value);
        setUncontrolledOpen(value);
        cancelDelayedClickAndHold();
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options not memoizable
    [options?.onOpenChange, setUncontrolledOpen, cancelDelayedClickAndHold]);
    // on close, cancel click-and-hold timers in flight and reset state
    useEffect(() => {
        if (!open) {
            cancelDelayedClickAndHold();
            isClickHold.current = false;
        }
    }, [open, cancelDelayedClickAndHold]);
    // Cancel any pointerup anywhere
    useEffect(() => {
        const handlePointerUp = () => cancelDelayedClickAndHold();
        return addListener(document.body, 'pointerup', handlePointerUp, true);
    }, [cancelDelayedClickAndHold]);
    return useMemo(() => ({
        manager: {
            open,
            setOpen,
            id,
            anchorRef: anchorRef ?? anchorRefFallback,
            isClickHold,
            openSubMenu(target) {
                rootRef.current?.openSubMenu(target);
            },
            private: { rootRef, triggerId },
        },
        getTriggerProps(props) {
            // Alert user of misconfiguration, where they are controlling this
            // trigger ref, but not specifying a menu anchor. This is almost
            // impossible to lint against, and will result in broken menu alignment.
            if (props?.ref && !options?.anchorRef) {
                throw new Error(`FPL error: if manually passing a ref into getTriggerProps(), you must also provide \`useMenu({ anchorRef: myRef })\`.`);
            }
            return mergeProps(props, {
                onClick(e) {
                    // This onClick is unlikely to get called with the mouse realistically, because the
                    // onPointerDown will open the menu with the overlay, and the onPointerUp will be
                    // triggered on the overlay instead.
                    // This will get called with keyboard enter/space though.
                    setOpen(true);
                    // Clear the timeout, but don't set a click-and-drag timeout on regular click events
                    cancelDelayedClickAndHold();
                    accept(e);
                },
                onPointerDown(e) {
                    e.currentTarget.focus();
                    setOpen(true);
                    if (e.pointerType === 'mouse')
                        delayedClickAndHold();
                    accept(e);
                },
                // Note: if a user has specified their own anchor ref, we can assume they DON’T want that to be the button.
                // Further, if they’ve manually specified a `ref` for other reasons, then they MUST also supply an anchorRef too
                ref: props?.ref ?? anchorRefFallback,
                'aria-controls': id,
                'aria-haspopup': 'menu',
                'aria-expanded': open,
                id: triggerId,
            });
        },
    }), [
        anchorRef,
        cancelDelayedClickAndHold,
        delayedClickAndHold,
        id,
        isClickHold,
        open,
        options?.anchorRef,
        rootRef,
        setOpen,
        triggerId,
    ]);
}

export { useMenu };
//# sourceMappingURL=menu-v2.api.js.map
